import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import api from '../services/api';

const SpeechSynthesis = () => {
  const [text, setText] = useState('This is a test of the Zest Speech Synthesis Service. It supports multiple high-quality Chinese neural network voices.');
  const [voice, setVoice] = useState('Cherry');
  const [rate, setRate] = useState('+0%');
  const [volume, setVolume] = useState('+0%');
  const [generating, setGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [configSuccess, setConfigSuccess] = useState('');
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const audioRef = useRef(null);

  // TTS Configuration
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('https://dashscope.aliyuncs.com/api/v1');
  const [model, setModel] = useState('qwen3-tts-flash');
  const [showConfig, setShowConfig] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [playingVoice, setPlayingVoice] = useState(null);

  // Load TTS configuration from localStorage on component mount
  useEffect(() => {
    try {
      const savedConfig = localStorage.getItem('tts_config');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        if (config.apiKey) setApiKey(config.apiKey);
        if (config.baseUrl) setBaseUrl(config.baseUrl);
        if (config.model) setModel(config.model);
        console.log('[TTS] Loaded configuration from localStorage');
      }
    } catch (error) {
      console.error('[TTS] Failed to load configuration:', error);
    }
  }, []);

  // Save TTS configuration to localStorage
  const handleSaveConfig = () => {
    try {
      const config = {
        apiKey,
        baseUrl,
        model
      };
      localStorage.setItem('tts_config', JSON.stringify(config));
      setConfigSuccess('Configuration saved successfully!');
      console.log('[TTS] Configuration saved to localStorage');

      // Hide success message after 3 seconds
      setTimeout(() => {
        setConfigSuccess('');
      }, 3000);
    } catch (error) {
      console.error('[TTS] Failed to save configuration:', error);
      setError('Failed to save configuration');
    }
  };

  // Update audio source when audioUrl changes
  useEffect(() => {
    console.log('[TTS] useEffect triggered, audioUrl:', audioUrl);
    console.log('[TTS] audioRef.current:', audioRef.current);

    if (audioRef.current && audioUrl) {
      const audio = audioRef.current;

      // Reset states
      setAudioLoading(true);
      setAudioReady(false);

      // Build the full URL - use base URL directly for audio files
      const baseUrl = 'https://api.getaizest.com';
      const fullUrl = `${baseUrl}${audioUrl}`;

      console.log('[TTS] Loading audio from:', fullUrl);

      // Event handlers
      const handleLoadedData = () => {
        console.log('[TTS] ✅ Audio loaded, duration:', audio.duration);
        setAudioLoading(false);
        setAudioReady(true);
      };

      const handleError = (e) => {
        console.error('[TTS] ❌ Audio error:', audio.error);
        console.error('[TTS] Error code:', audio.error?.code);
        console.error('[TTS] Error message:', audio.error?.message);
        setAudioLoading(false);
        setAudioReady(false);
      };

      const handleCanPlay = () => {
        console.log('[TTS] ▶️ Can play');
      };

      // Remove old listeners if any
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);

      // Add new listeners
      audio.addEventListener('loadeddata', handleLoadedData);
      audio.addEventListener('error', handleError);
      audio.addEventListener('canplay', handleCanPlay);

      // Set source and load
      audio.src = fullUrl;
      audio.load();

      // Cleanup function
      return () => {
        audio.removeEventListener('loadeddata', handleLoadedData);
        audio.removeEventListener('error', handleError);
        audio.removeEventListener('canplay', handleCanPlay);
      };
    } else {
      console.log('[TTS] Skipping - audioRef or audioUrl is null');
      setAudioLoading(false);
      setAudioReady(false);
    }
  }, [audioUrl]);

  // Qwen TTS voices - Alibaba Cloud neural voices (17 voices for Qwen3-TTS-Flash)
  const qwenTTSVoices = {
    'Female Voices': [
      { value: 'Cherry', label: 'Cherry (甜美女声)' },
      { value: 'Jennifer', label: 'Jennifer (温柔女声)' },
      { value: 'Katerina', label: 'Katerina (成熟女声)' },
      { value: 'Jada', label: 'Jada (活力女声)' },
      { value: 'Sunny', label: 'Sunny (阳光女声)' },
      { value: 'Kiki', label: 'Kiki (可爱女声)' },
    ],
    'Male Voices': [
      { value: 'Ethan', label: 'Ethan (沉稳男声)' },
      { value: 'Ryan', label: 'Ryan (年轻男声)' },
      { value: 'Elias', label: 'Elias (磁性男声)' },
      { value: 'Dylan', label: 'Dylan (自然男声)' },
      { value: 'Marcus', label: 'Marcus (成熟男声)' },
      { value: 'Roy', label: 'Roy (浑厚男声)' },
      { value: 'Peter', label: 'Peter (稳重男声)' },
      { value: 'Rocky', label: 'Rocky (活力男声)' },
      { value: 'Eric', label: 'Eric (清晰男声)' },
    ],
    'Neutral Voices': [
      { value: 'Nofish', label: 'Nofish (中性音色)' },
      { value: 'Li', label: 'Li (中性音色)' },
    ]
  };

  // Detailed voice information for table display
  const voiceDetails = [
    {
      name: 'Cherry',
      voiceParam: 'Cherry',
      description: 'Sunny and positive, friendly and natural young lady.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Female'
    },
    {
      name: 'Ethan',
      voiceParam: 'Ethan',
      description: 'Standard Mandarin with a slight northern accent. Sunny, warm, energetic, and youthful.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Male'
    },
    {
      name: 'Nofish',
      voiceParam: 'Nofish',
      description: 'Designer who never rolls their eyes.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Neutral'
    },
    {
      name: 'Jennifer',
      voiceParam: 'Jennifer',
      description: 'Brand dubbing, movies, high-end beauty and fashion ads.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Female'
    },
    {
      name: 'Ryan',
      voiceParam: 'Ryan',
      description: 'Fresh and cheerful, balancing playfulness and tension.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Male'
    },
    {
      name: 'Katerina',
      voiceParam: 'Katerina',
      description: 'Mature voice, elegant yet somewhat reserved.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Female'
    },
    {
      name: 'Elias',
      voiceParam: 'Elias',
      description: 'Professional and knowledgeable, approachable but slightly distant, confident yet restrained.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Male'
    },
    {
      name: 'Jada',
      voiceParam: 'Jada',
      description: 'Passionate Shanghainese girl.',
      languages: 'Chinese (Shanghai), English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Female'
    },
    {
      name: 'Dylan',
      voiceParam: 'Dylan',
      description: 'Straightforward boy from Beijing.',
      languages: 'Chinese (Beijing), English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Male'
    },
    {
      name: 'Marcus',
      voiceParam: 'Marcus',
      description: 'Deep and melancholic middle-aged man, immersive low-frequency voice.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Male'
    },
    {
      name: 'Sunny',
      voiceParam: 'Sunny',
      description: 'Sweet loli voice, younger than Cherry.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Female'
    },
    {
      name: 'Roy',
      voiceParam: 'Roy',
      description: 'Powerful and heroic male voice, energetic with a deep tone.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Male'
    },
    {
      name: 'Peter',
      voiceParam: 'Peter',
      description: 'Mature, steady and reliable male voice.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Male'
    },
    {
      name: 'Kiki',
      voiceParam: 'Kiki',
      description: 'Clear and cute loli voice.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Female'
    },
    {
      name: 'Rocky',
      voiceParam: 'Rocky',
      description: 'Young and energetic male voice with vitality.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Male'
    },
    {
      name: 'Eric',
      voiceParam: 'Eric',
      description: 'Clear and natural male voice, suitable for professional narration.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Male'
    },
    {
      name: 'Li',
      voiceParam: 'Li',
      description: 'Neutral and balanced voice tone.',
      languages: 'Chinese, English, French, German, Russian, Italian, Spanish, Portuguese, Japanese, Korean',
      gender: 'Neutral'
    },
  ];

  const handleGenerate = async () => {
    if (!text.trim()) {
      setError('Please enter text to generate speech');
      return;
    }

    if (!apiKey.trim()) {
      setError('Please configure API Key first');
      setShowConfig(true);
      return;
    }

    setGenerating(true);
    setError('');
    setSuccess('');

    console.log('[TTS Frontend] Sending request with voice:', voice, 'model:', model, 'baseUrl:', baseUrl);

    try {
      const response = await api.post('/admin/tts/generate', {
        text,
        voice,
        rate,
        volume,
        apiKey,
        model,
        baseUrl
      });

      if (response.data.success) {
        setAudioUrl(response.data.data.audioUrl);
        setSuccess('Speech generated successfully!');
      } else {
        setError(response.data.error?.message || 'Failed to generate speech');
      }
    } catch (err) {
      console.error('TTS generation error:', err);
      setError(err.response?.data?.error?.message || 'Failed to generate speech: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Speech Synthesis" />
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto">
            {/* Service Header Card */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg shadow-sm border-2 border-green-200 p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="bg-green-100 p-3 rounded-full">
                  <svg viewBox="0 0 1024 1024" width="32" height="32">
                    <path d="M983.80805 312.704889a510.271362 510.271362 0 0 0-109.823862-162.687797A511.103361 511.103361 0 0 0 512.00064 0.00128a508.799364 508.799364 0 0 0-361.983548 150.015812A511.103361 511.103361 0 0 0 0.00128 512.00064a508.799364 508.799364 0 0 0 150.015812 361.983548A511.103361 511.103361 0 0 0 512.00064 1023.872a508.799364 508.799364 0 0 0 199.295751-40.19195 508.351365 508.351365 0 0 0 162.687797-109.695862A511.103361 511.103361 0 0 0 1023.872 512.00064c0-69.119914-13.375983-136.19183-40.06395-199.295751z m-55.295931 375.167531a451.583436 451.583436 0 0 1-96.895879 143.74382 451.583436 451.583436 0 0 1-143.74382 96.895879A448.38344 448.38344 0 0 1 512.00064 964.032075a448.38344 448.38344 0 0 1-175.87178-35.519956 451.583436 451.583436 0 0 1-143.74382-96.895879 451.583436 451.583436 0 0 1-96.895879-143.74382A448.38344 448.38344 0 0 1 60.033205 512.00064c0-60.991924 11.839985-120.19185 35.455956-175.87178a451.583436 451.583436 0 0 1 96.895879-143.74382 451.583436 451.583436 0 0 1 143.74382-96.895879A448.38344 448.38344 0 0 1 512.00064 60.033205c60.991924 0 120.19185 11.839985 175.87178 35.455956 53.823933 22.783972 102.207872 55.423931 143.74382 96.895879a451.583436 451.583436 0 0 1 96.895879 143.74382c23.61597 55.67993 35.519956 114.879856 35.519956 175.87178 0 60.991924-11.903985 120.19185-35.519956 175.87178z" fill="#10b981" />
                    <path d="M264.00095 416.00076a30.079962 30.079962 0 0 0-30.015962 30.015962V577.920558c0 16.511979 13.503983 30.015962 30.015962 30.015962a30.079962 30.079962 0 0 0 30.015962-30.015962V446.080722a30.079962 30.079962 0 0 0-30.015962-30.015962z m124.031845-63.99992a30.079962 30.079962 0 0 0-30.079962 30.015962v259.967676a30.079962 30.079962 0 0 0 60.031925 0V382.080802a30.079962 30.079962 0 0 0-29.951963-30.015962zM512.00064 256.00096a30.079962 30.079962 0 0 0-30.015962 30.015962v451.967436c0 16.511979 13.503983 30.015962 30.015962 30.015962a30.079962 30.079962 0 0 0 30.015962-30.015962V286.080922A30.079962 30.079962 0 0 0 512.00064 256.00096z m247.99969 159.9998a30.079962 30.079962 0 0 0-30.015962 30.015962V577.920558c0 16.511979 13.503983 30.015962 30.015962 30.015962a30.079962 30.079962 0 0 0 30.015962-30.015962V446.080722a30.079962 30.079962 0 0 0-30.015962-30.015962z m-124.031845-63.99992a30.079962 30.079962 0 0 0-29.951963 30.015962v259.967676c0 16.511979 13.439983 30.015962 30.015963 30.015962a30.079962 30.079962 0 0 0 29.951963-30.015962V382.080802a30.079962 30.079962 0 0 0-30.015963-30.015962z" fill="#10b981" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-2xl font-bold text-gray-900">Qwen TTS Speech Synthesis</h3>
                    <button
                      onClick={() => setShowConfig(!showConfig)}
                      className="px-4 py-2 bg-white border-2 border-green-300 text-green-700 rounded-lg font-medium hover:bg-green-50 transition flex items-center gap-2"
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                        <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                      </svg>
                      {showConfig ? 'Hide' : 'Configure'}
                    </button>
                  </div>
                  <p className="text-gray-700 mb-3">
                    High-quality text-to-speech service powered by Alibaba Cloud Qwen TTS. Natural-sounding Chinese voices with qwen3-tts-flash model.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">✓ API Key Required</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">✓ 5 Chinese Voices</span>
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-semibold">✓ Neural Quality</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Configuration Panel */}
            {showConfig && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-green-600">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                  TTS Configuration
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Base URL *
                    </label>
                    <select
                      value={baseUrl}
                      onChange={(e) => setBaseUrl(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="https://dashscope.aliyuncs.com/api/v1">Beijing (China)</option>
                      <option value="https://dashscope-intl.aliyuncs.com/api/v1">Singapore (International)</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">API region endpoint</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Model *
                    </label>
                    <input
                      type="text"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="qwen3-tts-flash"
                    />
                    <p className="mt-1 text-xs text-gray-500">Qwen TTS model name</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key *
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? "text" : "password"}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter Alibaba Cloud API Key"
                    />
                    {apiKey && (
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showApiKey ? (
                          <svg viewBox="0 0 1024 1024" width="20" height="20">
                            <path d="M332.8 729.6l34.133333-34.133333c42.666667 12.8 93.866667 21.333333 145.066667 21.333333 162.133333 0 285.866667-68.266667 375.466667-213.333333-46.933333-72.533333-102.4-128-166.4-162.133334l29.866666-29.866666c72.533333 42.666667 132.266667 106.666667 183.466667 192-98.133333 170.666667-243.2 256-426.666667 256-59.733333 4.266667-119.466667-8.533333-174.933333-29.866667z m-115.2-64c-51.2-38.4-93.866667-93.866667-132.266667-157.866667 98.133333-170.666667 243.2-256 426.666667-256 38.4 0 76.8 4.266667 110.933333 12.8l-34.133333 34.133334c-25.6-4.266667-46.933333-4.266667-76.8-4.266667-162.133333 0-285.866667 68.266667-375.466667 213.333333 34.133333 51.2 72.533333 93.866667 115.2 128l-34.133333 29.866667z m230.4-46.933333l29.866667-29.866667c8.533333 4.266667 21.333333 4.266667 29.866666 4.266667 46.933333 0 85.333333-38.4 85.333334-85.333334 0-12.8 0-21.333333-4.266667-29.866666l29.866667-29.866667c12.8 17.066667 17.066667 38.4 17.066666 64 0 72.533333-55.466667 128-128 128-17.066667-4.266667-38.4-12.8-59.733333-21.333333zM384 499.2c4.266667-68.266667 55.466667-119.466667 123.733333-123.733333 0 4.266667-123.733333 123.733333-123.733333 123.733333zM733.866667 213.333333l29.866666 29.866667-512 512-34.133333-29.866667L733.866667 213.333333z" fill="#444444" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 1024 1024" width="20" height="20">
                            <path d="M512 298.666667c-162.133333 0-285.866667 68.266667-375.466667 213.333333 89.6 145.066667 213.333333 213.333333 375.466667 213.333333s285.866667-68.266667 375.466667-213.333333c-89.6-145.066667-213.333333-213.333333-375.466667-213.333333z m0 469.333333c-183.466667 0-328.533333-85.333333-426.666667-256 98.133333-170.666667 243.2-256 426.666667-256s328.533333 85.333333 426.666667 256c-98.133333 170.666667-243.2 256-426.666667 256z m0-170.666667c46.933333 0 85.333333-38.4 85.333333-85.333333s-38.4-85.333333-85.333333-85.333333-85.333333 38.4-85.333333 85.333333 38.4 85.333333 85.333333 85.333333z m0 42.666667c-72.533333 0-128-55.466667-128-128s55.466667-128 128-128 128 55.466667 128 128-55.466667 128-128 128z" fill="#444444" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Get your API Key from <a href="https://help.aliyun.com/zh/model-studio/get-api-key" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">DashScope Console</a>
                  </p>
                </div>

                {/* Save Configuration Button */}
                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleSaveConfig}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Configuration
                  </button>
                </div>
              </div>
            )}

            {/* Configuration Success Message - Below TTS Configuration Panel */}
            {configSuccess && (
              <div className="mb-6 bg-green-50 border-2 border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">{configSuccess}</span>
              </div>
            )}

            {/* TTS Generation Panel */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Generate Speech</h4>

              {/* Text Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text to Convert *
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  rows="4"
                  placeholder="Enter the text you want to convert to speech..."
                  maxLength="1000"
                />
                <p className="mt-1 text-xs text-gray-500">{text.length}/1000 characters</p>
              </div>

              {/* Voice Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Voice *
                  </label>
                  <select
                    value={voice}
                    onChange={(e) => setVoice(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {Object.entries(qwenTTSVoices).map(([category, voices]) => (
                      <optgroup key={category} label={category}>
                        {voices.map(v => (
                          <option key={v.value} value={v.value}>{v.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Speech Rate
                  </label>
                  <select
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="-50%">0.5x (Very Slow)</option>
                    <option value="-25%">0.75x (Slow)</option>
                    <option value="+0%">1.0x (Normal)</option>
                    <option value="+25%">1.25x (Fast)</option>
                    <option value="+50%">1.5x (Very Fast)</option>
                  </select>
                </div>
              </div>

              {/* Volume Control */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Volume
                </label>
                <select
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="-50%">50% (Quiet)</option>
                  <option value="-25%">75% (Soft)</option>
                  <option value="+0%">100% (Normal)</option>
                  <option value="+25%">125% (Loud)</option>
                  <option value="+50%">150% (Very Loud)</option>
                </select>
              </div>

              {/* Error/Success Messages */}
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                  {success}
                </div>
              )}

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={generating || !text.trim()}
                className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Generating Speech...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                    Generate Speech
                  </>
                )}
              </button>

              {/* Audio Player */}
              {audioUrl && (
                <div className="mt-6 bg-green-50 border-2 border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-semibold text-green-800">Generated Audio</span>
                    {audioReady && (
                      <span className="ml-auto flex items-center gap-1.5 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Ready
                      </span>
                    )}
                  </div>

                  {/* Loading State - Show on top of audio player */}
                  {audioLoading && (
                    <div className="bg-white border-2 border-green-300 rounded-lg p-8 mb-3 text-center">
                      <div className="flex flex-col items-center justify-center gap-4">
                        <div className="relative">
                          <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-200"></div>
                          <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent absolute top-0 left-0"></div>
                        </div>
                        <div className="text-center">
                          <p className="text-base font-medium text-gray-700">Generating audio...</p>
                          <p className="text-sm text-gray-500 mt-1">Please wait while we prepare your audio file</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Audio Player - Always rendered but hidden during loading */}
                  <div className={audioLoading ? 'hidden' : ''}>
                    <audio
                      ref={audioRef}
                      controls
                      className="w-full"
                      preload="auto"
                    >
                      Your browser does not support the audio element.
                    </audio>

                    <p className="mt-3 text-xs text-gray-600">
                      Voice: <span className="font-medium">
                        {Object.values(qwenTTSVoices).flat().find(v => v.value === voice)?.label || voice}
                      </span>
                      {' • '}Rate: <span className="font-medium">{rate}</span>
                      {' • '}Volume: <span className="font-medium">{volume}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Voice List Table */}
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h4 className="text-lg font-bold text-gray-900">Available Voices</h4>
                <p className="text-sm text-gray-600 mt-1">Explore all 17 voices supported by Qwen3-TTS-Flash</p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Voice Name</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Voice Parameter</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Voice Effect</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Supported Languages</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {voiceDetails.map((voice, index) => (
                      <tr key={voice.voiceParam} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{voice.name}</div>
                          <div className="text-xs text-gray-500">{voice.gender}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <code className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-mono">{voice.voiceParam}</code>
                        </td>
                        <td className="px-6 py-4">
                          <audio
                            id={`audio-${voice.voiceParam}`}
                            src={`https://api.getaizest.com/audio/voice-samples/${voice.voiceParam}.wav`}
                            preload="metadata"
                            style={{ display: 'none' }}
                            onPlay={() => setPlayingVoice(voice.voiceParam)}
                            onPause={() => setPlayingVoice(null)}
                            onEnded={() => setPlayingVoice(null)}
                          />
                          <button
                            onClick={() => {
                              const audio = document.getElementById(`audio-${voice.voiceParam}`);
                              if (audio) {
                                if (playingVoice === voice.voiceParam && !audio.paused) {
                                  // Pause if currently playing
                                  audio.pause();
                                } else {
                                  // Pause all other audio elements
                                  document.querySelectorAll('audio').forEach(a => {
                                    if (a !== audio) {
                                      a.pause();
                                    }
                                  });
                                  // Play the selected audio
                                  audio.currentTime = 0;
                                  audio.play();
                                }
                              }
                            }}
                            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                              playingVoice === voice.voiceParam
                                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                : 'bg-green-100 text-green-800 hover:bg-green-200'
                            }`}
                          >
                            {playingVoice === voice.voiceParam ? (
                              <>
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Pause
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                                Preview
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-700 max-w-md">{voice.description}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-xs text-gray-600">{voice.languages}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeechSynthesis;
